import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as htmlToImage from 'html-to-image';
import AdminLayout from '../../components/layout/AdminLayout';
import { FLYER_TEMPLATES, FlyerCanvas, FLYER_W, FLYER_H, FONT_PAIRS } from '../../components/flyer/FlyerTemplates';
import FlyerEditor from '../../components/flyer/FlyerEditor';
import {
  Loader2, Sparkles, Image as ImageIcon, Wand2, Download, Save, Plus, Trash2, Check, X,
  Palette, CalendarDays, LayoutTemplate, FolderOpen, Upload, Pencil, Type, Maximize2,
  Star, Images, RefreshCw, QrCode, SlidersHorizontal, Eye,
} from 'lucide-react';
import { makeQrDataUrl, QR_DOT_STYLES, QR_EYE_STYLES } from '../../lib/qr';
import StudioSuiteNav from '../../components/studio/StudioSuiteNav';
import BrandProfileManager from '../../components/studio/BrandProfileManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });
const errMsg = (e, f) => { const d = e?.response?.data?.detail; return !d ? f : (typeof d === 'string' ? d : d.message || f); };

const TABS = [
  { id: 'create', label: 'Create Flyer', icon: Wand2 },
  { id: 'editor', label: 'Flyer Editor', icon: Pencil },
  { id: 'brand', label: 'Brand Profile', icon: Palette },
  { id: 'themes', label: 'Themes', icon: CalendarDays },
  { id: 'references', label: 'References', icon: Images },
  { id: 'flyers', label: 'My Flyers', icon: FolderOpen },
];

export default function FlyerStudio({ embedded = false }) {
  const [tab, setTab] = useState('create');
  const [brands, setBrands] = useState([]);
  const [themes, setThemes] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState('');
  const [editorSeed, setEditorSeed] = useState(null);

  const openEditor = useCallback((seed) => { setEditorSeed(seed || {}); setTab('editor'); }, []);

  useEffect(() => {
    const raw = localStorage.getItem('dca_flyer_import');
    if (!raw) return;
    try {
      const s = JSON.parse(raw);
      setEditorSeed(s);
      setTab('editor');
      toast.info('Imported your design into the Flyer Editor — add text, QR, logo or contact info.');
    } catch (e) { /* ignore */ }
    localStorage.removeItem('dca_flyer_import');
  }, []);

  const loadBrands = useCallback(async () => {
    const r = await api.get('/api/flyer-studio/brands');
    setBrands(r.data.brands || []);
    if (!activeBrandId && r.data.brands?.length) setActiveBrandId(r.data.brands.find((b) => b.is_default)?.brand_id || r.data.brands[0].brand_id);
  }, [activeBrandId]);
  const loadThemes = useCallback(async () => {
    const r = await api.get('/api/flyer-studio/themes');
    setThemes(r.data.themes || []);
  }, []);

  useEffect(() => { loadBrands(); loadThemes(); }, []); // eslint-disable-line

  const activeBrand = brands.find((b) => b.brand_id === activeBrandId) || brands[0];

  const Wrap = embedded ? 'div' : AdminLayout;
  return (
    <Wrap>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="flyer-studio">
        <StudioSuiteNav active="flyer" embedded={embedded} />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0F1C] flex items-center gap-2"><LayoutTemplate className="text-[#1E5BA8]" size={24} /> Flyer Studio</h1>
            <p className="text-sm text-[#64748B] mt-1">Agency-grade daily flyers — your photo library, logo &amp; contact info, AI backgrounds &amp; copy.</p>
          </div>
          <div className="flex items-center gap-2">
            {brands.length > 0 && (
              <select value={activeBrandId} onChange={(e) => setActiveBrandId(e.target.value)} data-testid="flyer-brand-select"
                className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-semibold">
                {brands.map((b) => <option key={b.brand_id} value={b.brand_id}>{b.name}{b.is_default ? ' ★' : ''}</option>)}
              </select>
            )}
          </div>
        </div>

        <div className="flex gap-1 border-b border-[#E2E8F0] mb-6 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`flyer-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === t.id ? 'border-[#1E5BA8] text-[#1E5BA8]' : 'border-transparent text-[#64748B] hover:text-[#0A0F1C]'}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {brands.length === 0 && !['brand', 'references', 'editor'].includes(tab) ? (
          <EmptyBrand onGo={() => setTab('brand')} />
        ) : (
          <>
            {tab === 'create' && <CreateFlyer brand={activeBrand} themes={themes} onSaved={() => setTab('flyers')} />}
            {tab === 'editor' && <FlyerEditor brand={activeBrand} brands={brands} initialImageUrl={editorSeed?.image_url || ''} initialTitle={editorSeed?.title || ''} planId={editorSeed?.plan_id || null} itemId={editorSeed?.item_id || null} onSaved={() => setTab('flyers')} />}
            {tab === 'brand' && <BrandProfileManager onChanged={loadBrands} />}
            {tab === 'themes' && <ThemesTab themes={themes} reload={loadThemes} />}
            {tab === 'references' && <ReferencesTab />}
            {tab === 'flyers' && <MyFlyers brandId={activeBrandId} onEdit={openEditor} />}
          </>
        )}
      </div>
    </Wrap>
  );
}

function EmptyBrand({ onGo }) {
  return (
    <div className="text-center py-20 border-2 border-dashed border-[#E2E8F0] rounded-2xl" data-testid="flyer-empty">
      <Palette className="mx-auto text-[#94A3B8]" size={40} />
      <p className="mt-3 font-bold text-[#0A0F1C]">Set up your Brand Kit first</p>
      <p className="text-sm text-[#64748B] mt-1">Add your logo, headshots and contact info — then generate flyers in seconds.</p>
      <button onClick={onGo} data-testid="flyer-go-brand" className="mt-4 px-5 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94]">Go to Brand Kit</button>
    </div>
  );
}

/* ============================ CREATE FLYER ============================ */
function CreateFlyer({ brand, themes, onSaved }) {
  const [themeId, setThemeId] = useState('');
  const [customTheme, setCustomTheme] = useState('');
  const [coverageFocus, setCoverageFocus] = useState('');
  const [extra, setExtra] = useState('');
  const [copy, setCopy] = useState(null);
  const [bgUrl, setBgUrl] = useState('');
  const [bgOptions, setBgOptions] = useState([]);
  const [bgStyle, setBgStyle] = useState('photorealistic');
  const [bgCount, setBgCount] = useState(1);
  const [templateId, setTemplateId] = useState(FLYER_TEMPLATES[0].id);
  const [fontId, setFontId] = useState(FONT_PAIRS[0].id);
  const [hscale, setHscale] = useState(1);
  const [headshotId, setHeadshotId] = useState('');
  const [busy, setBusy] = useState('');
  const [proofOpen, setProofOpen] = useState(false);
  const [layout, setLayout] = useState({ logoScale: 1, headshotScale: 1, disclaimerAlign: 'left' });
  const [qr, setQr] = useState({ enabled: false, url: '', position: 'bottom-right', size: 200, label: 'Scan me', color: '', bgColor: '#FFFFFF', dotStyle: 'square', eyeStyle: 'square', useLogo: false, track: false, campaign: '', trackCode: '', scans: null, dataUrl: '' });
  const [aiEdit, setAiEdit] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [week, setWeek] = useState(null);
  const [myTemplates, setMyTemplates] = useState([]);
  const [qrDefaultSaved, setQrDefaultSaved] = useState(false);
  const [defaultTplId, setDefaultTplId] = useState(null);
  const appliedDefaultRef = useRef(null);
  const exportRef = useRef(null);

  // Load this owner's saved style templates.
  const loadTemplates = useCallback(async () => {
    try { const r = await api.get('/api/flyer-studio/flyer-templates'); setMyTemplates(r.data.templates || []); }
    catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // When the active brand has a saved QR default, seed the QR styling from it.
  useEffect(() => {
    const s = brand?.qr_style;
    if (s && Object.keys(s).length) {
      setQr((cur) => ({ ...cur, ...s, dataUrl: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.brand_id]);

  const saveQrDefault = async () => {
    if (!brand?.brand_id) { toast.error('Pick a brand first'); return; }
    const { dataUrl, ...style } = qr; // eslint-disable-line no-unused-vars
    try {
      await api.post(`/api/flyer-studio/brands/${brand.brand_id}/qr-style`, { qr_style: style });
      setQrDefaultSaved(true); setTimeout(() => setQrDefaultSaved(false), 2500);
      toast.success('Saved as this brand\u2019s default QR style');
    } catch (e) { toast.error(errMsg(e, 'Could not save QR default')); }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Name this flyer template (style + layout will be reusable):', themeId ? '' : (customTheme || 'My flyer style'));
    if (!name) return;
    const { dataUrl, ...qrStyle } = qr; // eslint-disable-line no-unused-vars
    let preview = null;
    try {
      await new Promise((res) => setTimeout(res, 200));
      if (exportRef.current) preview = await htmlToImage.toPng(exportRef.current, { width: FLYER_W, height: FLYER_H, pixelRatio: 0.25, cacheBust: true });
    } catch (e) { /* preview optional */ }
    try {
      await api.post('/api/flyer-studio/flyer-templates', {
        name, brand_id: brand?.brand_id || null,
        style: { template_id: templateId, font_id: fontId, hscale, layout, qr: qrStyle, bg_style: bgStyle },
        preview_data_url: preview,
      });
      toast.success('Flyer template saved');
      loadTemplates();
    } catch (e) { toast.error(errMsg(e, 'Could not save template')); }
  };

  const applyTemplate = (t, silent) => {
    const s = t.style || {};
    if (s.template_id) setTemplateId(s.template_id);
    if (s.font_id) setFontId(s.font_id);
    if (s.hscale != null) setHscale(s.hscale);
    if (s.layout) setLayout((cur) => ({ ...cur, ...s.layout }));
    if (s.bg_style) setBgStyle(s.bg_style);
    if (s.qr) setQr((cur) => ({ ...cur, ...s.qr, url: cur.url || s.qr.url || '', dataUrl: '' }));
    if (!silent) toast.success(`Applied template "${t.name}"`);
  };

  // Auto-load a brand's chosen default template when that brand is selected.
  useEffect(() => { setDefaultTplId(brand?.default_flyer_template_id || null); }, [brand?.brand_id, brand?.default_flyer_template_id]);
  useEffect(() => {
    const did = brand?.default_flyer_template_id;
    if (!did || !myTemplates.length || appliedDefaultRef.current === brand?.brand_id) return;
    const t = myTemplates.find((x) => x.template_id === did);
    if (t) { appliedDefaultRef.current = brand.brand_id; applyTemplate(t, true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.brand_id, myTemplates]);

  const setDefaultTemplate = async (t) => {
    if (!brand?.brand_id) { toast.error('Pick a brand first'); return; }
    try {
      if (defaultTplId === t.template_id) {
        await api.delete(`/api/flyer-studio/brands/${brand.brand_id}/default-template`);
        setDefaultTplId(null);
        toast.success('Removed default template');
      } else {
        await api.post(`/api/flyer-studio/flyer-templates/${t.template_id}/default`, { brand_id: brand.brand_id });
        setDefaultTplId(t.template_id);
        appliedDefaultRef.current = brand.brand_id;
        toast.success(`"${t.name}" is now ${brand.name}'s default flyer template`);
      }
    } catch (e) { toast.error(errMsg(e, 'Could not set default')); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this flyer template?')) return;
    try { await api.delete(`/api/flyer-studio/flyer-templates/${id}`); setMyTemplates((s) => s.filter((t) => t.template_id !== id)); }
    catch (e) { toast.error('Could not delete template'); }
  };

  useEffect(() => {
    const raw = localStorage.getItem('dca_intel_seed');
    if (!raw) return;
    try {
      const s = JSON.parse(raw);
      setCustomTheme((s.title || '').slice(0, 90));
      setExtra(`Base this flyer on this news source: "${s.title}". ${(s.summary || '').slice(0, 220)} (Source: ${s.publisher || ''}). Turn it into a compelling, on-brand promotional angle with a strong CTA.`);
      toast.info('Loaded source from Content Intelligence — click Auto-generate.');
    } catch (e) { /* ignore */ }
    localStorage.removeItem('dca_intel_seed');
  }, []);

  const fonts = FONT_PAIRS.find((f) => f.id === fontId) || FONT_PAIRS[0];
  const headshots = brand?.headshots || [];
  const selectedHead = headshots.find((h) => h.headshot_id === headshotId);
  const effectiveBrand = { ...brand, headshot_url: selectedHead?.url || brand?.headshot_url };
  const fullLayout = { ...layout, headlineScale: hscale };

  // Trackable smart-link: when enabled, mint/refresh a short /api/r/{code} link that
  // counts scans, then encode THAT url in the QR instead of the raw destination.
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  useEffect(() => {
    if (!qr.enabled || !qr.track || !qr.url) return;
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.post('/api/flyer-studio/qr-links', { destination_url: qr.url, brand_id: brand?.brand_id || null, campaign: qr.campaign || '' });
        if (!cancel) setQr((s) => ({ ...s, trackCode: r.data.code, scans: r.data.scans }));
      } catch (e) { /* ignore */ }
    }, 600);
    return () => { cancel = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr.enabled, qr.track, qr.url, qr.campaign, brand?.brand_id]);

  useEffect(() => {
    if (!qr.enabled || !qr.url) { if (qr.dataUrl) setQr((s) => ({ ...s, dataUrl: '' })); return; }
    if (qr.track && !qr.trackCode) return; // wait for the smart-link before drawing
    const target = qr.track && qr.trackCode ? `${BACKEND}/api/r/${qr.trackCode}` : qr.url;
    let cancel = false;
    makeQrDataUrl(target, {
      color: qr.color || brand?.colors?.primary,
      bgColor: qr.bgColor,
      dotStyle: qr.dotStyle,
      eyeStyle: qr.eyeStyle,
      logo: qr.useLogo ? brand?.logo_url : undefined,
    }).then((d) => { if (!cancel) setQr((s) => ({ ...s, dataUrl: d })); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr.enabled, qr.url, qr.color, qr.bgColor, qr.dotStyle, qr.eyeStyle, qr.useLogo, qr.track, qr.trackCode, brand]);

  const applyAiEdit = async () => {
    if (!aiEdit.trim()) { toast.error('Type an edit instruction'); return; }
    setAiBusy(true);
    try {
      const r = await api.post('/api/flyer-studio/ai-edit', {
        instruction: aiEdit, layout: fullLayout, copy: copy || {},
        qr: { enabled: qr.enabled, position: qr.position, label: qr.label, url: qr.url },
      });
      const p = r.data || {};
      if (p.layout) { setLayout((s) => ({ ...s, ...p.layout })); if (p.layout.headlineScale != null) setHscale(p.layout.headlineScale); }
      if (p.copy) setCopy((c) => ({ ...(c || {}), ...p.copy }));
      if (p.qr) setQr((s) => ({ ...s, ...p.qr }));
      setAiEdit('');
      toast.success('AI applied your edits');
    } catch (e) { toast.error(errMsg(e, 'AI edit failed')); } finally { setAiBusy(false); }
  };

  const genCopy = async () => {
    setBusy('copy');
    try {
      const r = await api.post('/api/flyer-studio/generate-copy', {
        brand_id: brand.brand_id, theme_id: themeId || null, theme_text: themeId ? null : (customTheme || null),
        coverage_focus: coverageFocus || null, extra_instructions: extra || '',
      });
      setCopy(r.data);
      toast.success('Copy generated');
    } catch (e) { toast.error(errMsg(e, 'Copy generation failed')); } finally { setBusy(''); }
  };

  const autoGen = async () => {
    setBusy('auto');
    try {
      const r = await api.post('/api/flyer-studio/auto', {
        brand_id: brand.brand_id, theme_id: themeId || null, theme_text: themeId ? null : (customTheme || null),
        coverage_focus: coverageFocus || null, extra_instructions: extra || '', style: bgStyle, aspect: 'portrait',
      });
      setCopy(r.data.copy);
      setBgUrl(r.data.background_url);
      setBgOptions(r.data.background_url ? [r.data.background_url] : []);
      toast.success('Flyer generated — copy & background ready');
    } catch (e) { toast.error(errMsg(e, 'Auto-generate failed')); } finally { setBusy(''); }
  };

  const genBg = async () => {
    setBusy('bg');
    try {
      const prompt = copy?.background_prompt || customTheme || 'warm family home, soft natural light, protective and trustworthy';
      const r = await api.post('/api/flyer-studio/generate-background', {
        brand_id: brand.brand_id, background_prompt: prompt, aspect: 'portrait', style: bgStyle, count: bgCount,
      });
      const opts = r.data.backgrounds || (r.data.background_url ? [r.data.background_url] : []);
      setBgOptions(opts);
      setBgUrl(r.data.background_url);
      toast.success(opts.length > 1 ? `${opts.length} backgrounds generated — pick one` : 'Background generated');
    } catch (e) { toast.error(errMsg(e, 'Background generation failed')); } finally { setBusy(''); }
  };

  const genWeek = async () => {
    setBusy('week');
    try {
      const r = await api.post('/api/flyer-studio/generate-week', { brand_id: brand.brand_id, count: 7, style: bgStyle, backgrounds: true });
      setWeek(r.data);
      if (r.data.stopped_reason) toast.warning(r.data.stopped_reason);
      else toast.success(`Generated ${r.data.flyers?.length || 0} flyers for the week`);
    } catch (e) { toast.error(errMsg(e, 'Week generation failed')); } finally { setBusy(''); }
  };

  const exportFlyer = async (saveIt) => {
    if (!copy) { toast.error('Generate copy first'); return; }
    setBusy(saveIt ? 'save' : 'download');
    try {
      await new Promise((res) => setTimeout(res, 300));
      const dataUrl = await htmlToImage.toPng(exportRef.current, { width: FLYER_W, height: FLYER_H, pixelRatio: 1, cacheBust: true });
      if (saveIt) {
        await api.post('/api/flyer-studio/flyers', {
          brand_id: brand.brand_id, theme_id: themeId || null, template_id: templateId,
          title: copy.headline, data_url: dataUrl, copy, background_url: bgUrl,
        });
        toast.success('Flyer saved to My Flyers');
        onSaved();
      } else {
        const a = document.createElement('a');
        a.href = dataUrl; a.download = `${(copy.headline || 'flyer').replace(/\s+/g, '-')}.png`; a.click();
        toast.success('Flyer downloaded');
      }
    } catch (e) { toast.error('Could not export flyer — try regenerating the background'); } finally { setBusy(''); }
  };

  const previewCopy = copy || {
    headline: 'Protect What Matters Most', subhead: 'Peace of mind for your whole family',
    body: 'Affordable life & health coverage tailored to you. Let\u2019s build a plan that fits your budget and your goals.',
    cta: 'Call or text today for a FREE quote!', coverage_highlights: (brand?.coverage_options || []).slice(0, 5), theme_name: 'Daily Flyer',
  };
  const scale = 360 / FLYER_W;

  return (
    <div className="grid lg:grid-cols-[1fr_400px] gap-6" data-testid="flyer-create">
      {/* Controls */}
      <div className="space-y-5">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
          <h3 className="font-bold text-[#0A0F1C] mb-3 flex items-center gap-2"><CalendarDays size={17} className="text-[#1E5BA8]" /> 1. Pick a theme</h3>
          <select value={themeId} onChange={(e) => setThemeId(e.target.value)} data-testid="flyer-theme-select" className="w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm">
            <option value="">— Custom theme —</option>
            {themes.map((t) => <option key={t.theme_id} value={t.theme_id}>{t.emoji} {t.name}</option>)}
          </select>
          {!themeId && <input value={customTheme} onChange={(e) => setCustomTheme(e.target.value)} placeholder="e.g. Tax-Free Retirement, Back to School protection…" data-testid="flyer-custom-theme" className="w-full mt-2 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm" />}
          <input value={coverageFocus} onChange={(e) => setCoverageFocus(e.target.value)} placeholder="Spotlight a coverage (optional) e.g. Final Expense" data-testid="flyer-coverage-focus" className="w-full mt-2 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm" />
          <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Extra instructions (optional) e.g. mention a holiday special" data-testid="flyer-extra" className="w-full mt-2 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm" />
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={autoGen} disabled={!!busy} data-testid="flyer-auto" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#1E5BA8] to-[#7C3AED] text-white text-sm font-bold hover:opacity-90 disabled:opacity-60">
              {busy === 'auto' ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Auto-generate flyer
            </button>
            <button onClick={genCopy} disabled={!!busy} data-testid="flyer-gen-copy" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#1E5BA8] text-[#1E5BA8] text-sm font-bold hover:bg-[#EFF6FF] disabled:opacity-60">
              {busy === 'copy' ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={15} />} Copy only
            </button>
            <button onClick={genWeek} disabled={!!busy} data-testid="flyer-gen-week" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0F766E] text-white text-sm font-bold hover:bg-[#0d655e] disabled:opacity-60">
              {busy === 'week' ? <Loader2 size={15} className="animate-spin" /> : <CalendarDays size={15} />} Generate full week
            </button>
          </div>
          <p className="text-[11px] text-[#94A3B8] mt-2">“Full week” builds 7 on-brand flyers (≈60% Final Expense / Life, the rest IUL, annuities, health, ACA &amp; Medicare).</p>
        </div>

        {copy && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5" data-testid="flyer-copy-editor">
            <h3 className="font-bold text-[#0A0F1C] mb-3 flex items-center gap-2"><Pencil size={16} className="text-[#1E5BA8]" /> 2. Review &amp; edit copy</h3>
            {[['headline', 'Headline'], ['subhead', 'Subhead'], ['cta', 'Call to action']].map(([k, label]) => (
              <div key={k} className="mb-2">
                <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">{label}</label>
                <input value={copy[k] || ''} onChange={(e) => setCopy({ ...copy, [k]: e.target.value })} data-testid={`flyer-edit-${k}`} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
              </div>
            ))}
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Body</label>
            <textarea value={copy.body || ''} onChange={(e) => setCopy({ ...copy, body: e.target.value })} data-testid="flyer-edit-body" rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
          <h3 className="font-bold text-[#0A0F1C] mb-3 flex items-center gap-2"><ImageIcon size={17} className="text-[#7C3AED]" /> 3. Background</h3>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Style</label>
              <select value={bgStyle} onChange={(e) => setBgStyle(e.target.value)} data-testid="flyer-bg-style" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                {['photorealistic', 'cinematic', 'luxury', 'minimal', 'abstract'].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Variations</label>
              <select value={bgCount} onChange={(e) => setBgCount(Number(e.target.value))} data-testid="flyer-bg-count" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{`${n} option${n > 1 ? 's' : ''}`}</option>)}
              </select>
            </div>
          </div>
          <button onClick={genBg} disabled={busy === 'bg'} data-testid="flyer-gen-bg" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#7C3AED] text-white text-sm font-bold hover:bg-[#6D28D9] disabled:opacity-60">
            {busy === 'bg' ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} {bgOptions.length ? 'Regenerate background' : 'Generate background (AI)'}
          </button>
          {bgUrl && <span className="ml-2 text-xs text-emerald-600 font-semibold inline-flex items-center gap-1"><Check size={13} /> Ready</span>}
          {bgOptions.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-3" data-testid="flyer-bg-options">
              {bgOptions.map((u, i) => (
                <button key={i} onClick={() => setBgUrl(u)} data-testid={`flyer-bg-option-${i}`}
                  className={`relative rounded-lg overflow-hidden border-2 transition-all ${bgUrl === u ? 'border-[#7C3AED]' : 'border-transparent hover:border-[#C4B5FD]'}`}>
                  <img src={u} alt={`option ${i + 1}`} className="w-full aspect-[4/5] object-cover" />
                  {bgUrl === u && <span className="absolute top-1 right-1 bg-[#7C3AED] text-white rounded-full p-0.5"><Check size={11} /></span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Typography + headshot */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
          <h3 className="font-bold text-[#0A0F1C] mb-3 flex items-center gap-2"><Type size={17} className="text-[#1E5BA8]" /> 4. Typography &amp; photo</h3>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Font pairing</label>
          <select value={fontId} onChange={(e) => setFontId(e.target.value)} data-testid="flyer-font-select" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
            {FONT_PAIRS.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mt-3 block">Headline size: {Math.round(hscale * 100)}%</label>
          <input type="range" min="0.75" max="1.3" step="0.05" value={hscale} onChange={(e) => setHscale(Number(e.target.value))} data-testid="flyer-hscale" className="w-full mt-1 accent-[#1E5BA8]" />
          {headshots.length > 0 && (
            <div className="mt-3">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Headshot</label>
              <div className="flex gap-2 flex-wrap mt-1" data-testid="flyer-headshot-picker">
                <button onClick={() => setHeadshotId('')} data-testid="flyer-headshot-default"
                  className={`w-14 h-14 rounded-lg overflow-hidden border-2 ${!headshotId ? 'border-[#1E5BA8]' : 'border-[#E2E8F0]'}`}>
                  <img src={brand?.headshot_url} alt="default" className="w-full h-full object-cover" />
                </button>
                {headshots.map((h) => (
                  <button key={h.headshot_id} onClick={() => setHeadshotId(h.headshot_id)} data-testid={`flyer-headshot-${h.headshot_id}`}
                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 ${headshotId === h.headshot_id ? 'border-[#1E5BA8]' : 'border-[#E2E8F0]'}`}>
                    <img src={h.url} alt={h.label} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
          <h3 className="font-bold text-[#0A0F1C] mb-3 flex items-center gap-2"><LayoutTemplate size={17} className="text-[#1E5BA8]" /> 5. Template</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FLYER_TEMPLATES.map((t) => (
              <button key={t.id} onClick={() => setTemplateId(t.id)} data-testid={`flyer-template-${t.id}`}
                className={`px-3 py-2 rounded-lg border text-xs font-bold transition-all ${templateId === t.id ? 'border-[#1E5BA8] bg-[#EFF6FF] text-[#1E5BA8]' : 'border-[#E2E8F0] text-[#475569] hover:border-[#1E5BA8]'}`}>{t.name}</button>
            ))}
          </div>

          {/* Saved style templates — reuse a perfected flyer's style on new content */}
          <div className="border-t border-[#F1F5F9] mt-4 pt-3" data-testid="flyer-my-templates">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">My saved templates</span>
              <button onClick={saveAsTemplate} data-testid="flyer-save-template" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-[11px] font-bold hover:bg-[#1A4F94]"><Save size={12} /> Save current as template</button>
            </div>
            {myTemplates.length === 0 ? (
              <p className="text-[11px] text-[#94A3B8]">Perfect a flyer's layout, fonts, sizing & QR, then save it as a template to reuse on future content.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {myTemplates.map((t) => (
                  <div key={t.template_id} className={`group relative rounded-lg border overflow-hidden hover:border-[#1E5BA8] ${defaultTplId === t.template_id ? 'border-[#F59E0B] ring-1 ring-[#F59E0B]' : 'border-[#E2E8F0]'}`} data-testid={`flyer-template-card-${t.template_id}`}>
                    <button onClick={() => applyTemplate(t)} data-testid={`flyer-template-apply-${t.template_id}`} className="block w-full text-left">
                      {t.preview_url
                        ? <img src={t.preview_url} alt={t.name} className="w-full aspect-[4/5] object-cover" />
                        : <div className="w-full aspect-[4/5] bg-[#F1F5F9] flex items-center justify-center"><LayoutTemplate size={20} className="text-[#CBD5E1]" /></div>}
                      <div className="px-2 py-1.5 text-[11px] font-bold text-[#475569] truncate flex items-center gap-1">{defaultTplId === t.template_id && <Star size={11} className="text-[#F59E0B] fill-[#F59E0B] shrink-0" />}{t.name}</div>
                    </button>
                    <button onClick={() => setDefaultTemplate(t)} data-testid={`flyer-template-default-${t.template_id}`} title={defaultTplId === t.template_id ? 'Default for this brand' : 'Set as brand default'} className={`absolute top-1 left-1 bg-white/90 rounded p-0.5 ${defaultTplId === t.template_id ? 'text-[#F59E0B] opacity-100' : 'text-[#94A3B8] hover:text-[#F59E0B] opacity-0 group-hover:opacity-100'}`}>
                      <Star size={13} className={defaultTplId === t.template_id ? 'fill-[#F59E0B]' : ''} />
                    </button>
                    <button onClick={() => deleteTemplate(t.template_id)} data-testid={`flyer-template-del-${t.template_id}`} className="absolute top-1 right-1 bg-white/90 text-[#94A3B8] hover:text-[#DC2626] rounded p-0.5 opacity-0 group-hover:opacity-100" aria-label="Delete template"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 6. Branded QR code */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5" data-testid="flyer-qr-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0A0F1C] flex items-center gap-2"><QrCode size={17} className="text-[#1E5BA8]" /> 6. Branded QR code</h3>
            <label className="flex items-center gap-2 text-xs font-bold text-[#475569]"><input type="checkbox" checked={qr.enabled} onChange={(e) => setQr({ ...qr, enabled: e.target.checked })} data-testid="flyer-qr-enabled" /> Add QR</label>
          </div>
          {qr.enabled && (
            <div className="space-y-2.5">
              <input value={qr.url} onChange={(e) => setQr({ ...qr, url: e.target.value })} placeholder="Link the QR opens, e.g. https://calendly.com/you" data-testid="flyer-qr-url" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
              <input value={qr.label} onChange={(e) => setQr({ ...qr, label: e.target.value })} placeholder="QR caption e.g. Scan to book" data-testid="flyer-qr-label" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <select value={qr.position} onChange={(e) => setQr({ ...qr, position: e.target.value })} data-testid="flyer-qr-position" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                  {[['bottom-right', 'Bottom right'], ['bottom-left', 'Bottom left'], ['top-right', 'Top right'], ['top-left', 'Top left'], ['center', 'Center']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label className="flex items-center gap-2 text-xs font-semibold text-[#475569]">Size <input type="range" min="140" max="300" step="10" value={qr.size} onChange={(e) => setQr({ ...qr, size: Number(e.target.value) })} data-testid="flyer-qr-size" className="flex-1 accent-[#1E5BA8]" /></label>
              </div>

              {/* Advanced styling — pattern, eyes, colors & logo so the QR can be fully branded */}
              <div className="border-t border-[#F1F5F9] pt-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] mb-1.5">Style & branding</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-semibold text-[#475569]">Pattern</label>
                    <select value={qr.dotStyle} onChange={(e) => setQr({ ...qr, dotStyle: e.target.value })} data-testid="flyer-qr-dotstyle" className="w-full mt-0.5 px-2.5 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                      {QR_DOT_STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-[#475569]">Corner eyes</label>
                    <select value={qr.eyeStyle} onChange={(e) => setQr({ ...qr, eyeStyle: e.target.value })} data-testid="flyer-qr-eyestyle" className="w-full mt-0.5 px-2.5 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                      {QR_EYE_STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap mt-2.5">
                  <div className="flex items-center gap-1.5">
                    <input type="color" value={qr.color || brand?.colors?.primary || '#0A0F1C'} onChange={(e) => setQr({ ...qr, color: e.target.value })} data-testid="flyer-qr-color" className="w-8 h-8 rounded-lg border border-[#E2E8F0] cursor-pointer" />
                    <span className="text-[11px] font-semibold text-[#475569]">Code color</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input type="color" value={qr.bgColor === 'transparent' ? '#FFFFFF' : (qr.bgColor || '#FFFFFF')} onChange={(e) => setQr({ ...qr, bgColor: e.target.value })} disabled={qr.bgColor === 'transparent'} data-testid="flyer-qr-bgcolor" className="w-8 h-8 rounded-lg border border-[#E2E8F0] cursor-pointer disabled:opacity-40" />
                    <span className="text-[11px] font-semibold text-[#475569]">Background</span>
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[#475569]">
                    <input type="checkbox" checked={qr.bgColor === 'transparent'} onChange={(e) => setQr({ ...qr, bgColor: e.target.checked ? 'transparent' : '#FFFFFF' })} data-testid="flyer-qr-transparent" /> Transparent bg
                  </label>
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[#475569]">
                    <input type="checkbox" checked={qr.useLogo} onChange={(e) => setQr({ ...qr, useLogo: e.target.checked })} disabled={!brand?.logo_url} data-testid="flyer-qr-uselogo" /> Logo in center {!brand?.logo_url && <span className="text-[#CBD5E1]">(add a logo first)</span>}
                  </label>
                </div>
              </div>

              {/* Trackable smart-link — count scans per campaign */}
              <div className="border-t border-[#F1F5F9] pt-2.5">
                <label className="flex items-center gap-1.5 text-[11px] font-semibold text-[#475569]">
                  <input type="checkbox" checked={qr.track} onChange={(e) => setQr({ ...qr, track: e.target.checked, trackCode: '', scans: null })} data-testid="flyer-qr-track" /> Track scans (smart link)
                </label>
                {qr.track && (
                  <div className="mt-2 space-y-2">
                    <input value={qr.campaign} onChange={(e) => setQr({ ...qr, campaign: e.target.value })} placeholder="Campaign label e.g. June Medicare Flyer" data-testid="flyer-qr-campaign" className="w-full px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-xs" />
                    <div className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="text-[#64748B] truncate" data-testid="flyer-qr-trackurl">{qr.trackCode ? `${BACKEND}/api/r/${qr.trackCode}` : 'Generating smart link…'}</span>
                      <span className="inline-flex items-center gap-1 font-bold text-[#1E5BA8] shrink-0" data-testid="flyer-qr-scans"><Eye size={12} /> {qr.scans ?? 0} scans</span>
                    </div>
                    <p className="text-[10px] text-[#94A3B8]">The QR encodes the smart link; every scan redirects to your URL and is counted here & in QR Analytics.</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                {qr.dataUrl && <img src={qr.dataUrl} alt="QR preview" data-testid="flyer-qr-preview" className="w-16 h-16 rounded-lg border border-[#E2E8F0] bg-white" />}
                <p className="text-[11px] text-[#94A3B8] flex-1">{qr.url && !qr.dataUrl ? 'Generating…' : qr.dataUrl ? 'Ready ✓ — appears on your flyer.' : 'Enter a link to generate.'}</p>
                <button onClick={saveQrDefault} data-testid="flyer-qr-save-default" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#1E5BA8] text-[#1E5BA8] text-[11px] font-bold hover:bg-[#EFF6FF] whitespace-nowrap">
                  {qrDefaultSaved ? <Check size={12} /> : <Star size={12} />} {qrDefaultSaved ? 'Saved' : 'Save as brand default'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 7. Fine-tune layout + AI edit */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5" data-testid="flyer-layout-card">
          <h3 className="font-bold text-[#0A0F1C] mb-3 flex items-center gap-2"><SlidersHorizontal size={17} className="text-[#1E5BA8]" /> 7. Fine-tune layout</h3>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Logo size: {Math.round(layout.logoScale * 100)}%</label>
          <input type="range" min="0.5" max="2" step="0.05" value={layout.logoScale} onChange={(e) => setLayout({ ...layout, logoScale: Number(e.target.value) })} data-testid="flyer-logo-scale" className="w-full accent-[#1E5BA8]" />
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mt-2 block">Headshot size: {Math.round(layout.headshotScale * 100)}%</label>
          <input type="range" min="0.5" max="2" step="0.05" value={layout.headshotScale} onChange={(e) => setLayout({ ...layout, headshotScale: Number(e.target.value) })} data-testid="flyer-headshot-scale" className="w-full accent-[#1E5BA8]" />
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mt-2 block">Disclaimer alignment</label>
          <select value={layout.disclaimerAlign} onChange={(e) => setLayout({ ...layout, disclaimerAlign: e.target.value })} data-testid="flyer-disclaimer-align" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
            {['left', 'center', 'right'].map((a) => <option key={a} value={a}>{a[0].toUpperCase() + a.slice(1)}</option>)}
          </select>
        </div>
      </div>

      {/* Live preview */}
      <div className="lg:sticky lg:top-4 self-start">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold text-[#64748B] uppercase tracking-wide">Live preview</div>
            <button onClick={() => setProofOpen(true)} data-testid="flyer-proof-open" className="inline-flex items-center gap-1 text-xs font-bold text-[#1E5BA8] hover:underline"><Maximize2 size={13} /> Full proof</button>
          </div>
          <div className="mx-auto rounded-xl overflow-hidden shadow-lg" style={{ width: FLYER_W * scale, height: FLYER_H * scale }} data-testid="flyer-preview">
            <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: FLYER_W, height: FLYER_H }}>
              <FlyerCanvas templateId={templateId} brand={effectiveBrand} copy={previewCopy} backgroundUrl={bgUrl} fonts={fonts} scale={hscale} layout={fullLayout} qr={qr} />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => exportFlyer(true)} disabled={busy === 'save' || !copy} data-testid="flyer-save" className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94] disabled:opacity-50">
              {busy === 'save' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </button>
            <button onClick={() => exportFlyer(false)} disabled={busy === 'download' || !copy} data-testid="flyer-download" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-sm font-bold hover:border-[#1E5BA8] hover:text-[#1E5BA8] disabled:opacity-50">
              {busy === 'download' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} PNG
            </button>
          </div>
          {/* AI edit box */}
          <div className="mt-4 rounded-xl bg-[#F5F3FF] border border-[#DDD6FE] p-3" data-testid="flyer-ai-edit-box">
            <div className="text-xs font-bold text-[#6D28D9] mb-1.5 flex items-center gap-1.5"><Sparkles size={13} /> AI edit — describe a change</div>
            <textarea value={aiEdit} onChange={(e) => setAiEdit(e.target.value)} rows={2} data-testid="flyer-ai-edit-input"
              placeholder="e.g. make the logo 20% bigger, center the disclaimer, punchier headline, add QR bottom-left" className="w-full px-3 py-2 rounded-lg border border-[#DDD6FE] text-sm" />
            <button onClick={applyAiEdit} disabled={aiBusy} data-testid="flyer-ai-edit-apply" className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#7C3AED] text-white text-sm font-bold hover:bg-[#6D28D9] disabled:opacity-60">
              {aiBusy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Apply AI edit
            </button>
          </div>
        </div>
      </div>

      {/* Off-screen full-resolution render for export */}
      <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden>
        <FlyerCanvas innerRef={exportRef} templateId={templateId} brand={effectiveBrand} copy={previewCopy} backgroundUrl={bgUrl} fonts={fonts} scale={hscale} layout={fullLayout} qr={qr} />
      </div>

      {proofOpen && (
        <ProofModal onClose={() => setProofOpen(false)}>
          <FlyerCanvas templateId={templateId} brand={effectiveBrand} copy={previewCopy} backgroundUrl={bgUrl} fonts={fonts} scale={hscale} layout={fullLayout} qr={qr} />
        </ProofModal>
      )}

      {week && <WeekModal data={week} brand={effectiveBrand} templateId={templateId} fonts={fonts} hscale={hscale} layout={fullLayout} qr={qr} onClose={() => setWeek(null)} onSavedAll={() => { setWeek(null); onSaved(); }} />}
    </div>
  );
}

/* full-size proof preview */
function ProofModal({ children, onClose }) {
  useEffect(() => { const k = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  const s = Math.min(680 / FLYER_W, (window.innerHeight - 120) / FLYER_H);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="flyer-proof-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="relative">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white" aria-label="Close"><X size={22} /></button>
        <div className="rounded-xl overflow-hidden shadow-2xl" style={{ width: FLYER_W * s, height: FLYER_H * s }}>
          <div style={{ transform: `scale(${s})`, transformOrigin: 'top left', width: FLYER_W, height: FLYER_H }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

/* week-of-flyers result modal */
function WeekModal({ data, brand, templateId, fonts, hscale, layout, qr, onClose, onSavedAll }) {
  const flyers = data.flyers || [];
  const refs = useRef({});
  const [saving, setSaving] = useState(false);
  const s = 200 / FLYER_W;

  const saveAll = async () => {
    setSaving(true);
    try {
      let n = 0;
      for (let i = 0; i < flyers.length; i++) {
        const node = refs.current[i];
        if (!node) continue;
        await new Promise((res) => setTimeout(res, 250));
        const dataUrl = await htmlToImage.toPng(node, { width: FLYER_W, height: FLYER_H, pixelRatio: 1, cacheBust: true });
        await api.post('/api/flyer-studio/flyers', {
          brand_id: brand.brand_id, theme_id: flyers[i].theme_id || null, template_id: templateId,
          title: flyers[i].copy?.headline || flyers[i].theme_name, data_url: dataUrl, copy: flyers[i].copy, background_url: flyers[i].background_url,
        });
        n += 1;
      }
      toast.success(`Saved ${n} flyers to My Flyers`);
      onSavedAll();
    } catch (e) { toast.error('Could not save all flyers'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="flyer-week-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl p-6 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold inline-flex items-center gap-2"><CalendarDays size={18} className="text-[#0F766E]" /> Your week of flyers</h3>
          <button onClick={onClose} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#64748B] mb-4">{flyers.length} flyers ready. {data.stopped_reason ? `Stopped early: ${data.stopped_reason}` : 'Save them all to My Flyers, or download individually.'}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="flyer-week-grid">
          {flyers.map((fl, i) => (
            <div key={i} className="rounded-xl overflow-hidden border border-[#E2E8F0]" data-testid={`flyer-week-item-${i}`}>
              <div className="overflow-hidden" style={{ width: '100%', height: FLYER_H * s * (1) }}>
                <div className="rounded-lg overflow-hidden" style={{ width: FLYER_W * s, height: FLYER_H * s }}>
                  <div style={{ transform: `scale(${s})`, transformOrigin: 'top left', width: FLYER_W, height: FLYER_H }}>
                    <FlyerCanvas templateId={templateId} brand={brand} copy={fl.copy} backgroundUrl={fl.background_url} fonts={fonts} scale={hscale} layout={layout} qr={qr} />
                  </div>
                </div>
              </div>
              <div className="p-2 text-[11px] font-bold text-[#475569] truncate bg-white border-t border-[#F1F5F9]">{fl.theme_name}</div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]" data-testid="flyer-week-cancel">Close</button>
          <button onClick={saveAll} disabled={saving} data-testid="flyer-week-saveall" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0F766E] text-white text-sm font-bold hover:bg-[#0d655e] disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save all to My Flyers
          </button>
        </div>
        {/* off-screen full-res renders for export */}
        <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden>
          {flyers.map((fl, i) => (
            <FlyerCanvas key={i} innerRef={(el) => { refs.current[i] = el; }} templateId={templateId} brand={brand} copy={fl.copy} backgroundUrl={fl.background_url} fonts={fonts} scale={hscale} layout={layout} qr={qr} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================ BRAND KIT ============================ */
/* Brand Kit replaced by the unified BrandProfileManager (shared across all studios). */

function readFile(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

/* ============================ THEMES ============================ */
const CAT_COLORS = {
  final_expense: 'bg-[#FEF2F2] text-[#B91C1C]', life: 'bg-[#EFF6FF] text-[#1D4ED8]', iul: 'bg-[#F0FDF4] text-[#15803D]',
  annuities: 'bg-[#FEFCE8] text-[#A16207]', aca: 'bg-[#F0F9FF] text-[#0369A1]', medicare: 'bg-[#FAF5FF] text-[#7C3AED]',
  health: 'bg-[#ECFEFF] text-[#0E7490]', general: 'bg-[#F1F5F9] text-[#475569]',
};

function ThemesTab({ themes, reload }) {
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState('');
  const blank = { name: '', schedule_type: 'topic', schedule_value: '', angle: '', emoji: '', category: 'final_expense', weight: 5, enabled: true };

  const seed = async () => { setBusy('seed'); try { const r = await api.post('/api/flyer-studio/themes/seed-defaults'); toast.success(`Added ${r.data.seeded} themes`); reload(); } catch { toast.error('Could not seed'); } finally { setBusy(''); } };
  const sync = async () => { setBusy('sync'); try { const r = await api.post('/api/flyer-studio/themes/sync-defaults'); toast.success(r.data.added ? `Added ${r.data.added} new themes` : 'You already have all the latest themes'); reload(); } catch { toast.error('Could not sync'); } finally { setBusy(''); } };
  const save = async () => {
    try {
      if (editing.theme_id) await api.put(`/api/flyer-studio/themes/${editing.theme_id}`, editing);
      else await api.post('/api/flyer-studio/themes', editing);
      toast.success('Theme saved'); setEditing(null); reload();
    } catch (e) { toast.error(errMsg(e, 'Could not save theme')); }
  };
  const del = async (id) => { try { await api.delete(`/api/flyer-studio/themes/${id}`); reload(); } catch { toast.error('Could not delete'); } };

  return (
    <div data-testid="flyer-themes">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-[#64748B]">Your editable theme calendar — weighted ~60% to Final Expense &amp; Life. AI writes copy in your voice for each.</p>
        <div className="flex gap-2">
          {themes.length === 0
            ? <button onClick={seed} disabled={!!busy} data-testid="flyer-seed-themes" className="px-3 py-2 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94]">Load default themes</button>
            : <button onClick={sync} disabled={!!busy} data-testid="flyer-sync-themes" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#1E5BA8] text-[#1E5BA8] text-sm font-bold hover:bg-[#EFF6FF]">{busy === 'sync' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync new themes</button>}
          <button onClick={() => setEditing(blank)} data-testid="flyer-add-theme" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-bold text-[#475569] hover:border-[#1E5BA8]"><Plus size={15} /> Add theme</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {themes.map((t) => (
          <div key={t.theme_id} className="bg-white rounded-xl border border-[#E2E8F0] p-4" data-testid={`flyer-theme-${t.theme_id}`}>
            <div className="flex items-start justify-between">
              <div className="font-bold text-[#0A0F1C]">{t.emoji} {t.name}</div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(t)} className="text-[#64748B] hover:text-[#1E5BA8]" data-testid={`theme-edit-${t.theme_id}`}><Pencil size={14} /></button>
                <button onClick={() => del(t.theme_id)} className="text-[#64748B] hover:text-[#DC2626]" data-testid={`theme-del-${t.theme_id}`}><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${CAT_COLORS[t.category] || CAT_COLORS.general}`}>{(t.category || 'general').replace('_', ' ')}</span>
              <span className="text-[10px] font-semibold text-[#94A3B8] uppercase tracking-wide">{t.schedule_type}{t.schedule_value ? ` · ${t.schedule_value}` : ''}</span>
            </div>
            <p className="text-xs text-[#475569] mt-2 line-clamp-3">{t.angle}</p>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="theme-modal" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-3"><h3 className="font-bold text-lg">{editing.theme_id ? 'Edit' : 'New'} theme</h3><button onClick={() => setEditing(null)}><X size={18} /></button></div>
            <div className="space-y-2">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Name (e.g. No Medical Exam Final Expense)" data-testid="theme-name" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="theme-category" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                  {['final_expense', 'life', 'iul', 'annuities', 'aca', 'medicare', 'health', 'general'].map((x) => <option key={x} value={x}>{x.replace('_', ' ')}</option>)}
                </select>
                <select value={editing.weight} onChange={(e) => setEditing({ ...editing, weight: Number(e.target.value) })} data-testid="theme-weight" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                  {[1, 2, 3, 4, 5].map((x) => <option key={x} value={x}>Frequency {x}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={editing.schedule_type} onChange={(e) => setEditing({ ...editing, schedule_type: e.target.value })} data-testid="theme-type" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                  {['weekly', 'monthly', 'holiday', 'topic', 'custom'].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <input value={editing.schedule_value} onChange={(e) => setEditing({ ...editing, schedule_value: e.target.value })} placeholder="monday / december / 2026-12-25" data-testid="theme-value" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
              </div>
              <input value={editing.emoji} onChange={(e) => setEditing({ ...editing, emoji: e.target.value })} placeholder="Emoji (optional) 💰" data-testid="theme-emoji" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
              <textarea value={editing.angle} onChange={(e) => setEditing({ ...editing, angle: e.target.value })} placeholder="Copy angle — what should this theme say?" rows={3} data-testid="theme-angle" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
              <button onClick={save} data-testid="theme-save" className="w-full px-4 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94]">Save theme</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================ REFERENCES ============================ */
function ReferencesTab() {
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState('');
  const fileInput = useRef(null);

  const load = useCallback(async () => { setLoading(true); try { const r = await api.get('/api/flyer-studio/references'); setRefs(r.data.references || []); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const dataUrl = await readFile(file);
        await api.post('/api/flyer-studio/references', { data_url: dataUrl, title: file.name, group: 'My References' });
      }
      toast.success('Reference flyer(s) saved'); load();
    } catch (e) { toast.error(errMsg(e, 'Upload failed')); } finally { setUploading(false); }
  };
  const del = async (id) => { try { await api.delete(`/api/flyer-studio/references/${id}`); setRefs((s) => s.filter((x) => x.ref_id !== id)); } catch { toast.error('Could not delete'); } };

  const groups = refs.reduce((acc, r) => { (acc[r.group || 'My References'] = acc[r.group || 'My References'] || []).push(r); return acc; }, {});

  return (
    <div data-testid="flyer-references">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-[#64748B]">Your locked-in style reference library. Use these as inspiration when you brief the AI — we aim to out-design them.</p>
        <input ref={fileInput} type="file" accept="image/*" multiple className="hidden" data-testid="ref-input" onChange={(e) => e.target.files.length && upload(e.target.files)} />
        <button onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="ref-upload-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94] disabled:opacity-60">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Add references
        </button>
      </div>
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" /></div>
      ) : refs.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#64748B]" data-testid="ref-empty">No references yet. Upload competitor or inspiration flyers to lock them in.</div>
      ) : (
        Object.entries(groups).map(([g, items]) => (
          <div key={g} className="mb-6">
            <div className="text-sm font-bold text-[#0A0F1C] mb-2">{g} <span className="text-[#94A3B8] font-semibold">({items.length})</span></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="ref-grid">
              {items.map((r) => (
                <div key={r.ref_id} className="relative rounded-xl overflow-hidden border border-[#E2E8F0] bg-white group" data-testid={`ref-card-${r.ref_id}`}>
                  <button onClick={() => setLightbox(r.image_url)} className="block w-full">
                    <img src={r.image_url} alt={r.title} className="w-full aspect-[4/5] object-cover" loading="lazy" />
                  </button>
                  <button onClick={() => del(r.ref_id)} className="absolute top-1 right-1 bg-white/90 text-[#94A3B8] hover:text-[#DC2626] rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Delete"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" data-testid="ref-lightbox" onClick={() => setLightbox('')}>
          <img src={lightbox} alt="reference" className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

/* ============================ MY FLYERS ============================ */
function MyFlyers({ brandId, onEdit }) {
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => { setLoading(true); try { const r = await api.get('/api/flyer-studio/flyers'); setFlyers(r.data.flyers || []); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { try { await api.delete(`/api/flyer-studio/flyers/${id}`); setFlyers((s) => s.filter((x) => x.flyer_id !== id)); } catch { toast.error('Could not delete'); } };

  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" /></div>;
  if (!flyers.length) return <div className="py-16 text-center text-sm text-[#64748B]" data-testid="flyers-empty">No flyers yet. Create one in the Create Flyer tab.</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="flyer-gallery">
      {flyers.map((fl) => (
        <div key={fl.flyer_id} className="rounded-xl overflow-hidden border border-[#E2E8F0] bg-white" data-testid={`flyer-card-${fl.flyer_id}`}>
          <img src={fl.image_url} alt={fl.title} className="w-full aspect-[4/5] object-cover" />
          <div className="p-2 flex items-center justify-between">
            <span className="text-[11px] font-bold text-[#475569] truncate">{fl.title}</span>
            <div className="flex gap-1.5 flex-shrink-0">
              <button onClick={() => onEdit?.({ image_url: fl.image_url, title: fl.title })} className="text-[#1E5BA8] hover:text-[#1A4F94]" aria-label="Edit" data-testid={`flyer-edit-${fl.flyer_id}`}><Pencil size={15} /></button>
              <a href={fl.image_url} download target="_blank" rel="noopener noreferrer" className="text-[#1E5BA8]" aria-label="Download"><Download size={15} /></a>
              <button onClick={() => del(fl.flyer_id)} className="text-[#94A3B8] hover:text-[#DC2626]" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
